/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { ExtendedTooltip, Spacer, Text, theme } from "@nordcloud/gnui";
import { EventActionAttempt } from "~/generated/graphql";
import { TooltipText } from "~/components";
import { dateFormat } from "~/constants";
import { isEmpty, isNil } from "~/tools";
import { LogsOutputButton } from "~/views/events/EventsDetails/components/LogOutputButton";
import {
  isNotFinishedExecution,
  hasExecutionStarted,
} from "~/views/events/EventsDetails/Tabs/Details/components/utils";
import { EventEntity } from "~/views/events/types";
import { getActionDisplayName } from "~/views/plans/utils";
import { ActionApprovalSection } from "./ActionApprovalSection";

type Props = {
  eventEntity: EventEntity;
  isBatchEntity: boolean;
  attempt?: EventActionAttempt;
};

export function ActionDetails({ eventEntity, isBatchEntity, attempt }: Props) {
  const startTime = getStartTime(eventEntity, isBatchEntity, attempt);
  const endTime = getEndTime(eventEntity, isBatchEntity, attempt);

  const isActionAwaitingCompletion = (actionEntity: EventEntity) => {
    return (
      isNil(actionEntity.globalState?.status) ||
      (actionEntity.globalState?.status &&
        isNotFinishedExecution(actionEntity?.globalState?.status))
    );
  };

  const isActionAwaitingStart = (actionEntity: EventEntity) => {
    return (
      isNil(startTime) ||
      (actionEntity.globalState?.status &&
        !hasExecutionStarted(actionEntity.globalState?.status))
    );
  };

  const isBatchAwaitingCompletion = (batch: EventEntity) => {
    return (
      isNotFinishedExecution(batch.status) ||
      batch?.actions?.some((batchAction) => {
        return isActionAwaitingCompletion(batchAction);
      })
    );
  };

  const isBatchAwaitingStart = (batch: EventEntity) => {
    return !hasExecutionStarted(batch.status);
  };

  const isAwaitingCompletion =
    !attempt?.state?.endTime &&
    (isBatchEntity
      ? isBatchAwaitingCompletion(eventEntity)
      : isActionAwaitingCompletion(eventEntity));

  const isAwaitingStart = isBatchEntity
    ? isBatchAwaitingStart(eventEntity)
    : isActionAwaitingStart(eventEntity);

  return (
    <div css={{ minHeight: "19.3rem" }}>
      <Text weight="medium">{isBatchEntity ? "Batch" : "Action"} Details</Text>
      <Text size="sm" tag="div">
        Name
      </Text>
      <Text>
        {getActionDisplayName(
          eventEntity.name,
          eventEntity.actionName || eventEntity.action?.name || "",
          eventEntity.type
        )}
      </Text>
      <Text size="sm" tag="div">
        Start Time (local)
      </Text>
      <If condition={isAwaitingStart}>
        <Then>
          <ExtendedTooltip
            caption={
              <>
                Start Time will appear when the{" "}
                {isBatchEntity ? "batch" : "action"} will be in progress
              </>
            }
          >
            <TooltipText
              size="md"
              tag="span"
              mr={theme.spacing.spacing01}
              weight="regular"
              color={theme.color.text.text01}
            >
              Awaiting start
            </TooltipText>
          </ExtendedTooltip>
        </Then>
        <Else>{startTime}</Else>
      </If>

      <Spacer height={theme.spacing.spacing04} />
      <Text size="sm" tag="div">
        End Time (local)
      </Text>
      <If condition={isAwaitingCompletion}>
        <Then>
          <ExtendedTooltip
            caption={
              <>
                End Time will appear when the{" "}
                {isBatchEntity ? "batch" : "action"} is complete
              </>
            }
          >
            <TooltipText
              size="md"
              tag="span"
              mr={theme.spacing.spacing01}
              weight="regular"
              color={theme.color.text.text01}
            >
              Awaiting completion
            </TooltipText>
          </ExtendedTooltip>
        </Then>
        <Else>{endTime}</Else>
      </If>

      <ActionApprovalSection
        eventEntity={eventEntity}
        isBatchEntity={isBatchEntity}
      />

      <When condition={eventEntity.globalState?.outputUri}>
        <LogsOutputButton
          actionLogLabel={eventEntity.name}
          outputUri={eventEntity.globalState?.outputUri}
        />
      </When>

      {/* TODO AP-4540}
      
      <When condition={isNotEmpty(eventEntity?.resourceStates ?? [])}>
        <Text size="sm" tag="div">
          Resource Status Summary
        </Text>
      </When> */}
    </div>
  );
}

const formatDate = (date?: string | null) => {
  return dayjs(date).format(dateFormat.dayMonthYearHourMinute);
};

const getStartTime = (
  eventEntity: EventEntity,
  isBatchEntity: boolean,
  attempt: EventActionAttempt | undefined
) => {
  if (isBatchEntity) {
    return formatDate(eventEntity.startTime);
  }

  if (attempt) {
    return formatDate(
      attempt.state?.startTime || attempt.state?.estimatedStartTime
    );
  }

  return formatDate(
    eventEntity.globalState?.startTime ??
      eventEntity.globalState?.estimatedStartTime
  );
};

const batchEndTime = (eventEntity: EventEntity): string | null => {
  const validTimes = eventEntity.actions
    ?.map((action) => action.globalState?.endTime)
    .filter((et): et is string => !!et);

  if (isNil(validTimes) || isEmpty(validTimes)) {
    return null;
  }

  return validTimes.reduce((max, curr) => (max > curr ? max : curr));
};

const getEndTime = (
  eventEntity: EventEntity,
  isBatchEntity: boolean,
  attempt: EventActionAttempt | undefined
) => {
  if (isBatchEntity) {
    return formatDate(batchEndTime(eventEntity));
  }

  if (attempt) {
    return formatDate(attempt.state?.endTime);
  }

  return formatDate(eventEntity.globalState?.endTime);
};
